/**
 * 分岐評価器
 *
 * シナリオの分岐条件を評価します。
 * ブランチステップの条件式やプリセットを安全に評価し、
 * 次の遷移先ノードを決定します。
 */

import type { BranchPreset, RunState } from 'shared';

// ============================================
// 安全性チェック用の定数
// ============================================

/** 安全なルート識別子 */
const SAFE_IDENTIFIER_ROOTS = new Set([
  'variables',
  'flags',
  'state',
  'true',
  'false',
  'null',
  'undefined',
]);

/** 禁止されたスニペット */
const DISALLOWED_SNIPPETS = [
  'constructor',
  'prototype',
  'window',
  'globalThis',
  'self',
  'Function',
  'process',
  'require',
  'import',
  'document',
  'eval',
  'this',
  '__proto__',
];

/** 危険な文字パターン */
const UNSAFE_CHARS_REGEX = /[^0-9a-zA-Z_.$<>=!&|()+\-*/?:,'" \t\r\n]/;

/** 識別子パターン */
const IDENTIFIER_REGEX = /[A-Za-z_][\w]*(?:\.[A-Za-z_][\w]*)*/g;

// ============================================
// ブランチステップ型（stepSchemaから抽出）
// ============================================

interface BranchRule {
  goto: string;
  label?: string;
  if?: string;
  preset?: BranchPreset;
}

interface BranchStep {
  type: 'branch';
  id: string;
  rules: BranchRule[];
  fallbackGoto?: string;
}

// ============================================
// BranchEvaluator クラス
// ============================================

/**
 * 分岐評価器
 */
export class BranchEvaluator {
  private state: RunState;

  constructor(state: RunState) {
    this.state = state;
  }

  /**
   * 状態を更新
   */
  updateState(state: RunState): void {
    this.state = state;
  }

  /**
   * 分岐を評価して遷移先ノードIDを返す
   */
  evaluateBranch(step: BranchStep): string | undefined {
    for (const rule of step.rules) {
      // プリセットを先に評価
      const presetResult = rule.preset
        ? this.applyBranchPreset(rule.preset)
        : undefined;

      // プリセットがundefinedの場合のみカスタム式を評価
      const expressionResult =
        presetResult === undefined && rule.if
          ? this.runSafeExpression(rule.if)
          : (presetResult ?? false);

      if (expressionResult) {
        return rule.goto;
      }
    }
    return undefined;
  }

  /**
   * プリセット条件を評価
   */
  applyBranchPreset(preset: BranchPreset): boolean | undefined {
    switch (preset.type) {
      case 'always':
        return true;

      case 'variable_equals': {
        return this.state.variables[preset.key] === preset.value;
      }

      case 'variable_gte': {
        const currentValue = Number(this.state.variables[preset.key]);
        if (Number.isNaN(currentValue)) {
          return false;
        }
        return currentValue >= preset.value;
      }

      // Phase A-h: 追加プリセット
      case 'variable_lte': {
        const currentValue = Number(this.state.variables[preset.key]);
        if (Number.isNaN(currentValue)) {
          return false;
        }
        return currentValue <= preset.value;
      }

      case 'variable_lt': {
        const currentValue = Number(this.state.variables[preset.key]);
        if (Number.isNaN(currentValue)) {
          return false;
        }
        return currentValue < preset.value;
      }

      case 'variable_gt': {
        const currentValue = Number(this.state.variables[preset.key]);
        if (Number.isNaN(currentValue)) {
          return false;
        }
        return currentValue > preset.value;
      }

      case 'variable_in': {
        const currentValue = this.state.variables[preset.key];
        return (preset as { type: 'variable_in'; key: string; values: unknown[] }).values.includes(currentValue);
      }

      case 'flag_equals':
        return this.state.flags[preset.key] === preset.value;

      case 'flag_is_true':
        return Boolean(this.state.flags[preset.key]);

      case 'flag_is_false':
        return !Boolean(this.state.flags[preset.key]);

      default:
        return undefined;
    }
  }

  /**
   * カスタム式を安全に評価
   */
  runSafeExpression(expression: string): boolean {
    if (!isExpressionSafe(expression)) {
      console.warn('[BranchEvaluator] Unsafe expression blocked:', expression);
      return false;
    }

    try {
      // eslint-disable-next-line no-new-func
      const fn = new Function(
        'variables',
        'flags',
        'state',
        `"use strict"; return Boolean(${expression});`
      );
      return !!fn(this.state.variables, this.state.flags, this.state);
    } catch (error) {
      console.warn('[BranchEvaluator] Expression evaluation error:', expression, error);
      return false;
    }
  }
}

// ============================================
// 安全性チェック関数
// ============================================

/**
 * 文字列リテラルを除去
 */
function stripStringLiterals(expression: string): string {
  return expression.replace(/(['"`])(?:(?=(\\?))\2.)*?\1/g, '');
}

/**
 * 式が安全かどうかをチェック
 */
export function isExpressionSafe(expression: string): boolean {
  const withoutStrings = stripStringLiterals(expression);

  // 危険な文字のチェック
  if (UNSAFE_CHARS_REGEX.test(withoutStrings)) {
    return false;
  }

  // 禁止スニペットのチェック
  const lowered = withoutStrings.toLowerCase();
  if (DISALLOWED_SNIPPETS.some((snippet) => lowered.includes(snippet.toLowerCase()))) {
    return false;
  }

  // 識別子のルートチェック
  const identifiers = withoutStrings.match(IDENTIFIER_REGEX) ?? [];
  return identifiers.every((identifier) => {
    const root = identifier.split('.')[0];
    return SAFE_IDENTIFIER_ROOTS.has(root);
  });
}

/**
 * 分岐評価器を作成
 */
export function createBranchEvaluator(state: RunState): BranchEvaluator {
  return new BranchEvaluator(state);
}
